/* eslint-disable max-depth */
import { throttle } from 'lodash-es';
import ModelNodesManager from './nodes-manager';
import { isObjectValueEqual, calOverlap, isVisible, checkHTMLElement } from './util/exposure';
import { BlockConfigParams, TransitionValue } from './type/btm-block';
import { defaultBlockConfig } from './constants';
import BtmCore from './btm-core';
import { ViewTimer } from './page-duration';
import { RemoteConfig } from './type/btm-core';
import { getBtmCode } from './util/actionBtm';

export const FULL_BTM_ID_ATTR = 'data-btm-id';
export const PART_BTM_ID_ATTR = 'data-btm';
export const CONFIG_ATTR = 'data-btm-config';

export default class ExposureCore {
  /** 曝光配置 */
  blockConfig?: BlockConfigParams;

  /** DOM变化监听对象 */
  private mutationObserver: MutationObserver | null = null;

  /** 滚动触发回调验证覆盖场景是否曝光 */
  private readonly scrollHandler: () => void = throttle(this.scrollObserver.bind(this), 300);

  /** 自身可视性临时监听池 */
  private readonly visibleTransitionPool: Map<HTMLElement, Set<HTMLElement>> = new Map();

  /** 覆盖临时监听池 */
  private readonly overlapTransitionPool: Map<HTMLElement, TransitionValue> = new Map();

  private readonly modelNodes: ModelNodesManager = new ModelNodesManager();

  /** 绑定的容器集合 */
  private readonly containers: Set<HTMLElement> = new Set();

  /** 监听器是否已经打开 */
  private hasOpenObserver = false;

  private intersectionObserver: IntersectionObserver | null = null;

  /** btm核心 */
  BtmCore?: BtmCore;

  private remoteConfig?: RemoteConfig;

  constructor(core: BtmCore, blockConfig: BlockConfigParams, remoteConfig?: RemoteConfig) {
    if (typeof window === 'undefined') {
      console.warn('不支持非浏览器环境');
      return;
    }
    this.BtmCore = core;
    this.remoteConfig = remoteConfig;
    this.blockConfig = { ...defaultBlockConfig, ...blockConfig };
    this.modelNodes.init(core, this.blockConfig, remoteConfig);
    const container = document.body;
    this.containers.add(container);
    this.openObserver(container);
  }

  /**
   * 发生点击事件
   * @param e
   */
  private readonly handleClickEvent = (e: Event) => {
    for (const container of this.containers) {
      // 如果被点击的元素不在容器内，则不再进行回溯判断上报。
      if (container.contains(e.target as HTMLElement)) {
        this.modelNodes?.checkAndHandleClickStatus(e.target as HTMLElement);
        break;
      }
    }
  };

  /**
   * 创建打开观察对象
   * @param container
   */
  private openObserver(container: HTMLElement = document.body) {
    try {
      // 没有打开过则创建观察曝光
      if (!this.hasOpenObserver) {
        this.createIntersectionObserver();
        this.dynamicNodesCollect();
        window?.addEventListener('scroll', this.scrollHandler, true);
        this.visibilityChange();
      }
      // 观察点击
      document.removeEventListener('click', this.handleClickEvent, true);
      document.addEventListener('click', this.handleClickEvent, true);
      this.domNodesCollect(container);
      this.hasOpenObserver = true;
    } catch (error) {
      console.error('BTM WEB SDK: 收集目标元素错误', error);
    }
  }

  private visibilityChange() {
    const viewTimer = new ViewTimer();
    const sendExpose = () => {
      this.modelNodes.exposeElements.forEach((node) => {
        if (this.modelNodes.checkIsExposed(node)) {
          this.reportExposureDuraiton(node);
        }
      });
    };
    viewTimer.trackVisibilityChange(() => {
      sendExpose();
    });
    viewTimer.trackBeforeUnload(() => {
      sendExpose();
    });
  }

  /**
   * 创建 IntersectionObserver 对象，可判断是否可视
   * @returns
   */
  private createIntersectionObserver() {
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('BTM WEB SDK：该设备不支持IntersectionObserver');
      return;
    }
    // 如果已经有了，就关闭重新建立一个
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.intersectionObserver = new IntersectionObserver(this.handleIntersectionObserve, {
      // 曝光触发阈值
      threshold: [this.blockConfig?.percent || defaultBlockConfig.percent]
    });
  }

  /**
   * 触发观察执行的回调函数
   * @param entries
   */
  private readonly handleIntersectionObserve: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      const { isIntersecting, target, intersectionRatio } = entry || {};
      if (isIntersecting) {
        if (this.modelNodes.checkExposureStatus(target as HTMLElement, intersectionRatio)) {
          if (this.checkAndHandleVisibility(target as HTMLElement)) {
            this.checkAndHandleOverlap(target as HTMLElement, true);
          }
        }
      } else {
        if (this.modelNodes.checkIsExposed(target as HTMLElement)) {
          this.reportExposureDuraiton(target as HTMLElement);
        }
      }
    });
  };

  private readonly checkAndHandleVisibility = (node: HTMLElement) => {
    // 1. 可视判断（是、否）
    // 2. 进入下一个判断（是）
    // 3. 属性变更监听，进入临时监听池（否）
    // 4. 补充曝光（否）
    // 5. 离开临时监听池，去除监听
    const { visibility, observeTargets } = isVisible(node);
    if (visibility) {
      return true;
    } else {
      // 进入自身可视性临时监听池
      observeTargets.forEach((observeTarget) => {
        const item = this.visibleTransitionPool.get(observeTarget);
        if (item) {
          item.add(node);
        } else {
          this.mutationObserver?.observe(observeTarget, {
            attributes: true
          });
          this.visibleTransitionPool.set(observeTarget, new Set<HTMLElement>().add(node));
        }
      });
      return false;
    }
  };

  /**
   * 上报曝光
   * @param node 曝光dom
   */
  private reportExposure(node: HTMLElement) {
    this.modelNodes.expose(node);
    // 防止有删除遗漏的情况
    this.overlapTransitionPool?.forEach((params, key) => {
      if (params.observeNodes.has(node)) {
        params.observeNodes.delete(node);
      }
      if (params.observeNodes.size === 0) {
        this.overlapTransitionPool.delete(key);
      }
    });
    this.visibleTransitionPool.forEach((params, key) => {
      if (params.has(node)) {
        params.delete(node);
      }
      if (params.size === 0) {
        this.visibleTransitionPool.delete(key);
      }
    });
  }
  /**
   * 上报曝光
   * @param node 曝光dom
   */
  private reportExposureDuraiton(node: HTMLElement) {
    const stillObserve = this.modelNodes.exposeDuration(node);
    // 防止有删除遗漏的情况
    this.overlapTransitionPool?.forEach((params, key) => {
      if (params.observeNodes.has(node)) {
        params.observeNodes.delete(node);
      }
      if (params.observeNodes.size === 0) {
        this.overlapTransitionPool.delete(key);
      }
    });
    this.visibleTransitionPool.forEach((params, key) => {
      if (params.has(node)) {
        params.delete(node);
      }
      if (params.size === 0) {
        this.visibleTransitionPool.delete(key);
      }
    });
    if (!stillObserve) {
      this.intersectionObserver?.unobserve(node);
    }
  }

  private readonly checkAndHandleOverlap = (
    node: HTMLElement,
    // 触发后第一次覆盖计算，如果没有覆盖元素则直接上报，不必再计算
    isFirstInterction = false
  ) => {
    // 1. 重叠计算（是、否）
    // 2. 曝光上报（是）
    // 3. 滚动监听、进入临时监听池，回到1（否）
    // 4. 覆盖元素属性监听，回到1（否）
    // 5. 离开临时监听池

    let isExpose = false;
    if (this.blockConfig?.isEnableOverlap) {
      const { intersectionRatio, overlapElements } = calOverlap(node, isFirstInterction);
      if (
        this.modelNodes.checkExposureStatus(node, intersectionRatio, true) ||
        // 第一次intersection触发后，且什么覆盖也没有就直接上报了，免去一部分计算
        intersectionRatio === -1
      ) {
        isExpose = true;
      } else {
        // 进入覆盖临时监听池
        overlapElements?.forEach((el) => {
          const { top, left, bottom, right } = el.getBoundingClientRect();
          const item = this.overlapTransitionPool.get(el);
          if (item) {
            item.observeNodes.add(node);
            item.oldValue = {
              top,
              left,
              bottom,
              right
            };
          } else {
            this.mutationObserver?.observe(el, { attributes: true });
            const transitionValue = {
              observeNodes: new Set<HTMLElement>().add(node),
              oldValue: {
                top,
                left,
                bottom,
                right
              }
            };
            this.overlapTransitionPool.set(el, transitionValue);
          }
        });
      }
    } else {
      isExpose = true;
    }
    if (isExpose) {
      this.reportExposure(node);
    }
  };

  /**
   * 观察节点
   * @param node
   */
  private readonly observeNode = (node: HTMLElement) => {
    // 记录收集到的 BTM 元素
    if (this.modelNodes.addObserveNode(node)) {
      this.intersectionObserver?.unobserve(node);
      this.intersectionObserver?.observe(node);
    }
  };

  /** 收集容器内目标元素，并观察元素 */
  private readonly domNodesCollect = (container: HTMLElement) => {
    try {
      const targetNodeAttrs: string[] = [];
      [CONFIG_ATTR, PART_BTM_ID_ATTR].forEach((item) => {
        targetNodeAttrs.push(`[${item}]`);
      });
      const getShouldReport = (item: HTMLElement) => {
        const config = JSON.parse(item.getAttribute(CONFIG_ATTR) || '{}');
        let shouldReport;
        if (typeof config.closeExpose !== 'undefined') {
          shouldReport = !config.closeExpose;
        } else {
          const info = this.BtmCore?.getFullBtmInfo(item as HTMLElement);
          if (!info) return true;
          const btmC = getBtmCode(info.btm, 'c') || 'c0';
          const inBlackList = this.remoteConfig?.moduleConfig?.disableModuleExpose?.includes(btmC);
          shouldReport = !inBlackList;
        }
        return shouldReport;
      };

      const nodeList = Array.from(container.querySelectorAll(targetNodeAttrs.join(','))).filter((item) =>
        getShouldReport(item as HTMLElement)
      );

      // 检查容器本身是否是目标元素
      if (
        [CONFIG_ATTR, PART_BTM_ID_ATTR].some((attr) => container.getAttribute(attr)) &&
        getShouldReport(container)
      ) {
        nodeList.push(container);
      }
      nodeList.forEach((node) => {
        this.observeNode(node as HTMLElement);
      });
    } catch (error) {
      console.error('BTM WEB SDK: 收集目标元素错误', error);
    }
  };

  /**
   * 创建MutationObserver对象
   * @returns
   */
  private dynamicNodesCollect() {
    try {
      if (typeof MutationObserver === 'undefined') {
        console.warn('该设备不支持MutationObserver');
        return;
      }
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          const { type, addedNodes, removedNodes, target, attributeName } = mutation;

          if (type === 'childList') {
            // 监听元素的新增
            for (const node of Array.from(addedNodes)) {
              if (checkHTMLElement(node)) {
                for (const container of this.containers) {
                  if (container.contains(node)) {
                    this.domNodesCollect(node);
                    break;
                  }
                }
              }
            }
            // 清理已不在DOM上的元素
            for (const node of Array.from(removedNodes)) {
              if (checkHTMLElement(node)) {
                // 如果container被移除，则清理掉
                if (this.containers.has(node)) {
                  this.containers.delete(node);
                }
                this.handleObserveNodes(node);
              }
            }
          } else if (type === 'attributes') {
            if (checkHTMLElement(target)) {
              // 虚拟列表 如果是data-btm-id/data-btm属性变化,那就重新收集
              if (
                attributeName &&
                [FULL_BTM_ID_ATTR, PART_BTM_ID_ATTR, CONFIG_ATTR].includes(attributeName)
              ) {
                for (const container of this.containers) {
                  if (container.contains(target)) {
                    this.observeNode(target);
                    break;
                  }
                }
              } else {
                // 处理因为属性变化导致中转池内元素可以曝光的场景
                // 延迟500ms是因为考虑到有transition的情况，暂且定为500ms，后续考虑用监听TransitionEnd来处理。
                setTimeout(() => {
                  this.handleObserveNodes(target, true);
                }, 500);
              }
            }
          }
        });
      });

      this.mutationObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [FULL_BTM_ID_ATTR, PART_BTM_ID_ATTR, CONFIG_ATTR]
      });
    } catch (error) {
      console.error('BTM WEB SDK: 节点变化回调错误', error);
    }
  }

  /** 处理在中转池中的元素
   *  @param {HTMLElement} target 被观察的元素
   *  @param {string} isAttribute 是否为因属性变化被调用（为了性能问题，覆盖场景的处理做一些判断后再进行计算）
   *
   */
  private handleObserveNodes(target: HTMLElement, isAttribute?: boolean) {
    try {
      // 如果是因为自身可视问题而在中转池的元素
      const visibleObserveNodes = this.visibleTransitionPool.get(target);
      if (visibleObserveNodes) {
        for (const el of visibleObserveNodes) {
          if (this.checkAndHandleVisibility(el)) {
            this.checkAndHandleOverlap(el);
          }
        }
      }

      // 如果是因为遮蔽问题而在中转池的元素
      const transitionValue = this.overlapTransitionPool.get(target);
      if (transitionValue) {
        const { observeNodes, oldValue } = transitionValue;
        // 如果覆盖元素的属性变化了，但是位置宽高，以及自身可视性都没有变化，那就不需要再进行下面的计算
        if (isAttribute) {
          const { top, left, bottom, right } = target.getBoundingClientRect();
          if (isVisible(target).visibility && isObjectValueEqual(oldValue, { top, left, bottom, right })) {
            return;
          }
        }
        if (observeNodes) {
          for (const el of observeNodes) {
            if (this.checkAndHandleVisibility(el)) {
              this.checkAndHandleOverlap(el);
            }
          }
        }
      }
    } catch (error) {
      console.error('BTM WEB SDK: 处理中转池元素错误。', error);
    }
  }

  /** 滚动时要监听被覆盖的元素是否变为可视 */
  private scrollObserver() {
    if (this.overlapTransitionPool) {
      this.overlapTransitionPool.forEach((_, key) => {
        this.handleObserveNodes(key);
      });
    }
  }
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6IiIsIm5hbWVzIjpbXSwic291cmNlcyI6W10sInNvdXJjZXNDb250ZW50IjpbXX0=